// src/pages/admin/DosageRules.jsx
import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import CrudPage from '../../components/admin/CrudPage';
import { dosageAPI, medicineAPI } from '../../services/api';

const Form = ({ form, onChange, medicines }) => (
  <>
    <div className="form-group">
      <label className="form-label required">Medicine</label>
      <select className="form-select" name="medicine_id" value={form.medicine_id || ''} onChange={onChange} required>
        <option value="">— Select Medicine —</option>
        {medicines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
    </div>

    <div className="form-row">
      <div className="form-group">
        <label className="form-label required">Dose per kg (mg/kg)</label>
        <input className="form-input" type="number" step="0.01" name="dose_per_kg" value={form.dose_per_kg || ''} onChange={onChange} placeholder="e.g. 15" required />
      </div>
      <div className="form-group">
        <label className="form-label">Unit</label>
        <select className="form-select" name="unit" value={form.unit || 'mg'} onChange={onChange}>
          <option value="mg">mg</option>
          <option value="mcg">mcg</option>
          <option value="g">g</option>
          <option value="mL">mL</option>
          <option value="units">units</option>
        </select>
      </div>
    </div>

    <div className="form-row">
      <div className="form-group">
        <label className="form-label">Min Dose</label>
        <input className="form-input" type="number" step="0.01" name="min_dose" value={form.min_dose || ''} onChange={onChange} placeholder="Minimum dose" />
      </div>
      <div className="form-group">
        <label className="form-label">Max Dose</label>
        <input className="form-input" type="number" step="0.01" name="max_dose" value={form.max_dose || ''} onChange={onChange} placeholder="Maximum dose" />
      </div>
    </div>

    <div className="form-group">
      <label className="form-label">Frequency</label>
      <input className="form-input" name="frequency" value={form.frequency || ''} onChange={onChange} placeholder="e.g. Every 8 hours" />
    </div>

    <div className="form-group">
      <label className="form-label">Doses per Day</label>
      <input className="form-input" type="number" name="frequency_per_day" value={form.frequency_per_day || ''} onChange={onChange} placeholder="e.g. 3" />
    </div>

    <div className="form-row">
      <div className="form-group">
        <label className="form-label">Min Age (months)</label>
        <input className="form-input" type="number" name="min_age_months" value={form.min_age_months || ''} onChange={onChange} placeholder="e.g. 0" />
      </div>
      <div className="form-group">
        <label className="form-label">Max Age (months)</label>
        <input className="form-input" type="number" name="max_age_months" value={form.max_age_months || ''} onChange={onChange} placeholder="Leave blank for no limit" />
      </div>
    </div>

    <div className="form-row">
      <div className="form-group">
        <label className="form-label">Min Weight (kg)</label>
        <input className="form-input" type="number" step="0.1" name="min_weight_kg" value={form.min_weight_kg || ''} onChange={onChange} />
      </div>
      <div className="form-group">
        <label className="form-label">Max Weight (kg)</label>
        <input className="form-input" type="number" step="0.1" name="max_weight_kg" value={form.max_weight_kg || ''} onChange={onChange} />
      </div>
    </div>

    <div className="form-group">
      <label className="form-label">Clinical Notes</label>
      <textarea className="form-textarea" name="notes" value={form.notes || ''} onChange={onChange} placeholder="Additional clinical notes…" />
    </div>
  </>
);

export default function DosageRules() {
  const [medicines, setMedicines] = useState([]);
  const [filterMedicine, setFilterMedicine] = useState('');

  useEffect(() => { medicineAPI.getAll().then(r => setMedicines(r.data.data)); }, []);

  return (
    <AdminLayout title="Dosage Rules">
      <CrudPage
        title="Dosage Rules"
        subtitle="Define weight-based dosage rules per medicine"
        searchKey="medicine_name"
        columns={[
          { key: 'medicine_name', label: 'Medicine' },
          { key: 'dose_per_kg',   label: 'Dose/kg', render: r => <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{r.dose_per_kg} {r.unit}/kg</span> },
          { key: 'min_dose',      label: 'Min Dose', render: r => r.min_dose ? `${r.min_dose} ${r.unit}` : '—' },
          { key: 'max_dose',      label: 'Max Dose', render: r => r.max_dose ? `${r.max_dose} ${r.unit}` : '—' },
          { key: 'frequency',     label: 'Frequency' },
          { key: 'min_age_months', label: 'Age Range', render: r => {
            const min = r.min_age_months != null ? `${r.min_age_months}m` : '0m';
            const max = r.max_age_months != null ? `${r.max_age_months}m` : '∞';
            return `${min} – ${max}`;
          }},
        ]}
        fetchFn={dosageAPI.getRules}
        createFn={dosageAPI.createRule}
        updateFn={dosageAPI.updateRule}
        deleteFn={dosageAPI.deleteRule}
        FormComponent={Form}
        formDefault={{ medicine_id: '', dose_per_kg: '', min_dose: '', max_dose: '', frequency: '', frequency_per_day: '', min_age_months: '', max_age_months: '', min_weight_kg: '', max_weight_kg: '', unit: 'mg', notes: '' }}
        extraProps={{ medicines }}
        renderFilters={() => (
          <select className="form-select" style={{ maxWidth: 200, margin: 0 }}
            value={filterMedicine} onChange={e => setFilterMedicine(e.target.value)}>
            <option value="">All Medicines</option>
            {medicines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        customFilter={row => filterMedicine ? String(row.medicine_id) === filterMedicine : true}
      />
    </AdminLayout>
  );
}
